import React, { useState } from "react";
import "./LegalPages.css";

function Legal({ initialTab = "terms" } = {}) {
  const [activeTab, setActiveTab] = useState(initialTab);

  return (
    <div className="legal-page-container">
      <div className="legal-page-content">
        <div className="legal-header">
          <h1>Legal</h1>
          <p className="legal-effective-date">
            Your rights and responsibilities while using Optimus OS
          </p>
        </div>

        {/* Toggle Tabs */}
        <div className="legal-tabs">
          <button
            className={`legal-tab ${activeTab === "terms" ? "active" : ""}`}
            onClick={() => setActiveTab("terms")}
          >
            Terms of Service
          </button>
          <button
            className={`legal-tab ${activeTab === "privacy" ? "active" : ""}`}
            onClick={() => setActiveTab("privacy")}
          >
            Privacy Policy
          </button>
        </div>

        {/* Terms of Service Content */}
        {activeTab === "terms" && (
          <div className="legal-body">
            <p className="legal-intro">
              This Terms of Service ("Agreement") applies to all digital
              properties owned by Optimus OS, Inc., a Delaware corporation,
              including but not limited to osoptimus.com, optimusmcp.com,
              activateoptimus.com, and any related subdomains, email content,
              applications, and programs.
            </p>

            <p className="legal-intro">
              By accessing or using any part of our websites, programs,
              products, events, or services ("Services"), you agree to be bound
              by this Agreement. If you do not accept these terms, you must not
              use our Services.
            </p>

            <div className="legal-toc">
              <h3>Table of Contents</h3>
              <ol>
                <li>Intellectual Property and Licenses</li>
                <li>Use of the Site</li>
                <li>Software and Code Use Disclaimer</li>
                <li>Disclaimers and Limitations of Liability</li>
                <li>Indemnification</li>
                <li>Third Party Rights</li>
                <li>Term and Termination</li>
                <li>Miscellaneous</li>
                <li>Affiliate Program Terms</li>
                <li>Contact Information</li>
              </ol>
            </div>

            <section className="legal-section">
              <h2>1. Intellectual Property and Licenses</h2>
              <p>
                All materials found on our websites and inside our
                programs—whether digital downloads, video, audio, written
                content, software, or other formats—are protected by copyright
                and trademark laws. You may not copy, distribute, modify,
                publish, transmit, display, or sell any content obtained from
                Optimus OS without express written permission.
              </p>
              <p>
                You grant Optimus OS a non-exclusive, royalty-free, perpetual
                license to reproduce, publish, and otherwise use any materials
                or feedback you provide, including on forums or via email, with
                full rights of sublicensing and attribution. This includes the
                right to use anonymized interaction data to develop and improve
                AI models that power our services.
              </p>
              <p>
                <strong>Non-Disclosure:</strong> You agree to keep confidential
                any private content or discussions found within exclusive
                programs, forums, masterminds, or member groups.
              </p>
            </section>

            <section className="legal-section">
              <h2>2. Use of the Site and Services</h2>
              <p>
                You agree not to use our Services for any unlawful or prohibited
                purpose. You are responsible for implementing safeguards for your
                own device, data, and security. We do not guarantee that
                downloadable files will be free from viruses or harmful
                components.
              </p>
              <p>
                We provide our Services "AS IS" without warranties of any kind.
                Your use is at your own risk. We do not guarantee any specific
                outcomes, earnings, or results.
              </p>
            </section>

            <section className="legal-section">
              <h2>3. Software and Code Use Disclaimer</h2>
              <p>
                Any software, automations, prompts, scripts, code, templates, or
                integrations provided by Optimus OS are offered "AS IS" and
                without support or warranty. This includes any no-code tools, AI
                workflows, MCP integrations, installations, or enhancements.
              </p>

              <h3>3A. Proprietary Software: Copyright, Licensing, and Restrictions</h3>
              <p>
                By using any software, templates, scripts, automation tools,
                macros, or custom code ("Proprietary Software") provided by
                Optimus OS, you agree to the following:
              </p>

              <h4>a. Intellectual Property & Ownership</h4>
              <p>
                All Proprietary Software is the exclusive intellectual property
                of Optimus OS, Inc. It is protected by U.S. and international
                copyright, trade secret, and intellectual property laws. You are
                granted a limited, revocable, non-transferable license to use the
                software solely on the device(s) for which it was provided. No
                ownership, resale, sublicensing, or derivative rights are
                granted.
              </p>

              <h4>b. Restrictions on Use</h4>
              <p>You expressly agree:</p>
              <ul>
                <li>
                  Not to reverse engineer, decompile, disassemble, or otherwise
                  attempt to derive the source code or underlying logic of any
                  Proprietary Software.
                </li>
                <li>
                  Not to reskin, clone, copy, duplicate, or repackage the
                  software in any form.
                </li>
                <li>
                  Not to disclose, share, or distribute the software, any part of
                  its structure, capabilities, or code to third parties without
                  written consent.
                </li>
                <li>
                  Not to use the software or its features to develop competing
                  products or services.
                </li>
                <li>
                  Not to train AI models or other agents using our code, prompts,
                  outputs, or logic without explicit permission.
                </li>
              </ul>

              <h4>c. Confidentiality and Competitive Protections</h4>
              <p>
                The functionality, design, capabilities, and internal structure of
                the software are considered confidential and proprietary. You
                agree not to:
              </p>
              <ul>
                <li>
                  Discuss, demonstrate, or disclose the capabilities of the
                  software to competitors or potential competitors.
                </li>
                <li>
                  Attempt to bypass or disable licensing restrictions, usage
                  monitoring, or embedded protections.
                </li>
              </ul>

              <h4>d. Revocation and Removal</h4>
              <p>
                Optimus OS, Inc. reserves the right to revoke your access and
                request removal of any software, code, or tools at any time. Upon
                request, you agree to immediately uninstall the software and
                delete all related components and documentation.
              </p>

              <h4>e. Liability and Risk Assumption</h4>
              <p>
                All software is provided AS IS without any warranties of
                performance, stability, compatibility, or support. You assume full
                responsibility for installing, using, or modifying the software.
                By proceeding, you waive all claims related to system damage, data
                loss, performance issues, or unintended outcomes.
              </p>
              <p className="legal-warning">
                <strong>
                  ⚠️ We strongly recommend you back up your system prior to any
                  installation.
                </strong>
              </p>
            </section>

            <section className="legal-section">
              <h2>4. Disclaimers and Limitations of Liability</h2>
              <p>
                We make no guarantees of performance, earnings, outcomes, or ROI.
                Examples shown are not guarantees of results. Any testimonials are
                not to be considered typical or guaranteed.
              </p>
              <p>
                No financial, legal, investment, or technical advice is provided.
                Consult professionals before making decisions based on our
                content. You are responsible for your results and interpretation.
              </p>
              <p>
                <strong>
                  All liability is expressly disclaimed to the fullest extent
                  permitted by law.
                </strong>
              </p>
            </section>

            <section className="legal-section">
              <h2>5. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless Optimus OS, Inc. and all
                related affiliates, staff, partners, and service providers from
                any claims, damages, or liabilities resulting from your use of the
                Services or violation of these terms.
              </p>
            </section>

            <section className="legal-section">
              <h2>6. Third Party Rights</h2>
              <p>
                All rights outlined above also extend to our affiliates,
                employees, licensors, and third-party contributors. These parties
                have the right to enforce terms directly.
              </p>
            </section>

            <section className="legal-section">
              <h2>7. Term and Termination</h2>
              <p>
                This Agreement remains in effect unless terminated by either
                party. Key provisions survive termination including intellectual
                property protections, disclaimers, indemnities, and limitations of
                liability.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. Miscellaneous</h2>
              <p>
                This Agreement is governed by the laws of the State of Delaware
                and the United States. Legal disputes must be filed in Delaware
                courts. No waiver of any right shall be construed as ongoing or
                permanent. We may assign this Agreement at any time without
                notice.
              </p>
            </section>

            <section className="legal-section">
              <h2>9. Affiliate Program Terms</h2>
              <p>
                If you participate in our affiliate program, you earn commission
                on qualifying referrals according to the terms displayed in your
                affiliate dashboard. Commission rates, payment schedules, and
                program rules are subject to change. We reserve the right to
                refuse payouts for fraudulent activity, policy violations, or
                chargebacks.
              </p>
              <p>
                You may not misrepresent our product or make false claims. All
                promotional materials must comply with FTC guidelines regarding
                affiliate disclosures.
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Contact Information</h2>
              <p>For questions regarding these Terms of Service, please contact us:</p>
              <div className="legal-contact">
                <p>
                  <strong>Optimus OS, Inc.</strong>
                </p>
                <p>Delaware Corporation</p>
                <p>
                  Email:{" "}
                  <a href="mailto:support@osoptimus.com">support@osoptimus.com</a>
                </p>
              </div>
            </section>

            <div className="legal-footer">
              <p>
                <strong>
                  COPYRIGHT © 2026 Optimus OS, Inc. – All Rights Reserved.
                </strong>
              </p>
              <p className="legal-update-note">Last Updated: May 15, 2026</p>
            </div>
          </div>
        )}

        {/* Privacy Policy Content */}
        {activeTab === "privacy" && (
          <div className="legal-body">
            <p className="legal-intro">
              At Optimus OS, Inc., we are committed to protecting your privacy
              and respecting your data. This Privacy Policy explains how we
              collect, use, share, and safeguard your personal information when
              you use our services.
            </p>

            <p className="legal-intro">
              By using our services, you agree to the collection and use of
              information in accordance with this policy. If you do not agree
              with our policies and practices, please do not use our services.
            </p>

            <div className="legal-toc">
              <h3>Table of Contents</h3>
              <ol>
                <li>Information We Collect</li>
                <li>How We Use Your Information</li>
                <li>Data Sharing and Disclosure</li>
                <li>Your Rights and Choices</li>
                <li>Data Security</li>
                <li>Data Retention</li>
                <li>International Data Transfers</li>
                <li>Children's Privacy</li>
                <li>Cookies and Tracking Technologies</li>
                <li>Changes to This Policy</li>
                <li>Contact Us</li>
              </ol>
            </div>

            <section className="legal-section">
              <h2>1. Information We Collect</h2>
              <p>
                We collect several types of information from and about users of
                our services:
              </p>

              <h3>a. Personal Information You Provide</h3>
              <ul>
                <li>
                  <strong>Account Information:</strong> Name, email address,
                  phone number, password
                </li>
                <li>
                  <strong>Payment Information:</strong> Billing address, payment
                  method details (processed securely through third-party payment
                  processors)
                </li>
                <li>
                  <strong>Profile Information:</strong> Business details,
                  preferences, settings
                </li>
                <li>
                  <strong>Communications:</strong> Messages, support tickets,
                  feedback, survey responses
                </li>
                <li>
                  <strong>API Credentials:</strong> Third-party service
                  integration keys (encrypted)
                </li>
              </ul>

              <h3>b. Information Collected Automatically</h3>
              <ul>
                <li>
                  <strong>Usage Data:</strong> Pages visited, features used, time
                  spent, click patterns
                </li>
                <li>
                  <strong>Device Information:</strong> IP address, browser type,
                  device type, operating system
                </li>
                <li>
                  <strong>Log Data:</strong> Server logs, error reports,
                  performance metrics
                </li>
                <li>
                  <strong>AI Interaction Data:</strong> Conversation messages, tool
                  usage patterns, and AI responses used to improve our services
                </li>
                <li>
                  <strong>Cookies:</strong> Session cookies, preference cookies,
                  analytics cookies
                </li>
              </ul>

              <h3>c. Information from Third Parties</h3>
              <ul>
                <li>
                  Data from integrated services (GoHighLevel, Apollo.io, etc.)
                  when you connect them
                </li>
                <li>Payment processing information from Stripe</li>
                <li>Analytics data from third-party tools</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>2. How We Use Your Information</h2>
              <p>
                We use the information we collect for the following purposes:
              </p>

              <h3>Service Delivery</h3>
              <ul>
                <li>Provide, maintain, and improve our services</li>
                <li>Process transactions and send related information</li>
                <li>Respond to your requests, questions, and support needs</li>
                <li>
                  Enable integrations with third-party services you authorize
                </li>
              </ul>

              <h3>Communication</h3>
              <ul>
                <li>
                  Send product updates, security alerts, and administrative
                  messages
                </li>
                <li>Deliver marketing communications (with your consent)</li>
                <li>
                  Send affiliate program notifications and commission updates
                </li>
              </ul>

              <h3>Analytics and Improvement</h3>
              <ul>
                <li>
                  Analyze usage patterns to improve features and user experience
                </li>
                <li>Monitor and analyze trends, usage, and activities</li>
                <li>Detect, prevent, and address technical issues</li>
                <li>Develop and improve AI models used to power our services</li>
                <li>Conduct research and development</li>
              </ul>

              <h3>Legal and Security</h3>
              <ul>
                <li>Comply with legal obligations and enforce our terms</li>
                <li>Protect our rights, privacy, safety, or property</li>
                <li>Prevent fraud and abuse</li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>3. Data Sharing and Disclosure</h2>
              <p>
                <strong>We do not sell or rent your personal information.</strong>{" "}
                We may share your information only in the following circumstances:
              </p>

              <h3>Service Providers</h3>
              <p>
                We share data with trusted third-party service providers who
                perform services on our behalf:
              </p>
              <ul>
                <li>
                  <strong>Payment Processors:</strong> Stripe (for payment
                  processing)
                </li>
                <li>
                  <strong>Email Services:</strong> Resend (for transactional
                  emails)
                </li>
                <li>
                  <strong>Infrastructure:</strong> Railway, Cloudflare (for
                  hosting and CDN)
                </li>
                <li>
                  <strong>Database:</strong> PostgreSQL hosting providers
                </li>
              </ul>

              <h3>Legal Requirements</h3>
              <p>
                We may disclose your information if required to do so by law or in
                response to:
              </p>
              <ul>
                <li>Valid legal processes (subpoenas, court orders)</li>
                <li>Governmental requests</li>
                <li>Protection of our rights and safety</li>
                <li>Investigation of fraud or security issues</li>
              </ul>

              <h3>Business Transfers</h3>
              <p>
                In the event of a merger, acquisition, or sale of assets, your
                information may be transferred to the acquiring entity.
              </p>

              <h3>With Your Consent</h3>
              <p>
                We may share your information with third parties when you
                explicitly consent to such sharing.
              </p>
            </section>

            <section className="legal-section">
              <h2>4. Your Rights and Choices</h2>
              <p>
                In accordance with GDPR, CCPA, and other privacy laws, you have
                the following rights:
              </p>

              <h3>Access and Portability</h3>
              <ul>
                <li>
                  <strong>Access:</strong> Request a copy of your personal data
                </li>
                <li>
                  <strong>Export:</strong> Download your data in a portable format
                  (JSON)
                </li>
                <li>
                  <strong>GDPR Export:</strong> Request a complete export via
                  Settings → Data & Privacy
                </li>
              </ul>

              <h3>Correction and Deletion</h3>
              <ul>
                <li>
                  <strong>Update:</strong> Correct inaccurate or incomplete
                  information in your profile
                </li>
                <li>
                  <strong>Delete:</strong> Request deletion of your account and
                  all associated data
                </li>
                <li>
                  <strong>Right to be Forgotten:</strong> Available via Settings →
                  Delete My Account
                </li>
              </ul>

              <h3>Communication Preferences</h3>
              <ul>
                <li>
                  <strong>Opt-Out:</strong> Unsubscribe from marketing emails at
                  any time
                </li>
                <li>
                  <strong>SMS:</strong> Reply STOP to opt-out of text messages
                </li>
                <li>
                  <strong>Notifications:</strong> Manage notification preferences
                  in Settings
                </li>
              </ul>

              <h3>Do Not Track</h3>
              <p>
                We honor Do Not Track signals and do not track, plant cookies, or
                use advertising when a DNT browser mechanism is in place.
              </p>

              <p className="legal-warning">
                <strong>
                  ⚠️ To exercise your rights, contact us at
                  support@osoptimus.com or use the tools in Settings → Data &
                  Privacy
                </strong>
              </p>
            </section>

            <section className="legal-section">
              <h2>5. Data Security</h2>
              <p>
                We implement commercially reasonable technical and organizational
                measures to protect your personal information:
              </p>

              <h3>Security Measures</h3>
              <ul>
                <li>
                  <strong>Encryption:</strong> Data encrypted at rest and in
                  transit (TLS/SSL)
                </li>
                <li>
                  <strong>Authentication:</strong> Secure password hashing
                  (bcrypt)
                </li>
                <li>
                  <strong>Access Controls:</strong> Role-based access and least
                  privilege principles
                </li>
                <li>
                  <strong>API Security:</strong> Rate limiting, input validation,
                  CORS protection
                </li>
                <li>
                  <strong>Monitoring:</strong> Real-time error tracking and
                  security alerts
                </li>
                <li>
                  <strong>Infrastructure:</strong> Secure cloud hosting with
                  automatic backups
                </li>
              </ul>

              <p>
                However, no method of transmission over the Internet or electronic
                storage is 100% secure. While we strive to protect your personal
                information, we cannot guarantee absolute security.
              </p>
            </section>

            <section className="legal-section">
              <h2>6. Data Retention</h2>
              <p>
                We retain your personal information for as long as necessary to
                provide our services and fulfill the purposes outlined in this
                policy:
              </p>

              <ul>
                <li>
                  <strong>Active Accounts:</strong> Retained while your account is
                  active
                </li>
                <li>
                  <strong>Deleted Accounts:</strong> Permanently deleted within 30
                  days of deletion request
                </li>
                <li>
                  <strong>AI Training Data:</strong> Interaction logs retained up to
                  24 months, anonymized before use in model development
                </li>
                <li>
                  <strong>Audit Logs:</strong> Retained for 7 years (legal
                  requirement)
                </li>
                <li>
                  <strong>Backups:</strong> Backup data purged after 90 days
                </li>
                <li>
                  <strong>Legal Hold:</strong> Data may be retained longer if
                  required by law
                </li>
              </ul>
            </section>

            <section className="legal-section">
              <h2>7. International Data Transfers</h2>
              <p>
                Your information may be transferred to and maintained on servers
                located outside of your state, province, country, or other
                governmental jurisdiction where data protection laws may differ.
              </p>
              <p>
                Our servers are located in the United States. By using our
                services, you consent to the transfer of your information to the
                U.S. and processing in accordance with this Privacy Policy.
              </p>
              <p>
                We comply with applicable data protection laws regarding
                international transfers, including GDPR requirements for transfers
                outside the European Economic Area.
              </p>
            </section>

            <section className="legal-section">
              <h2>8. Children's Privacy</h2>
              <p>
                Our services are not intended for children under 13 years of age.
                We do not knowingly collect personal information from children
                under 13. If you are a parent or guardian and believe your child
                has provided us with personal information, please contact us
                immediately.
              </p>
            </section>

            <section className="legal-section">
              <h2>9. Cookies and Tracking Technologies</h2>
              <p>
                We use cookies and similar tracking technologies to track activity
                on our service and store certain information:
              </p>

              <h3>Types of Cookies We Use</h3>
              <ul>
                <li>
                  <strong>Essential Cookies:</strong> Required for the service to
                  function (session management)
                </li>
                <li>
                  <strong>Preference Cookies:</strong> Remember your settings and
                  preferences
                </li>
                <li>
                  <strong>Analytics Cookies:</strong> Help us understand how you
                  use our service
                </li>
                <li>
                  <strong>Security Cookies:</strong> Used for security features
                  and fraud prevention
                </li>
              </ul>

              <h3>Managing Cookies</h3>
              <p>
                You can instruct your browser to refuse all cookies or indicate
                when a cookie is being sent. However, if you do not accept
                cookies, you may not be able to use some portions of our service.
              </p>
            </section>

            <section className="legal-section">
              <h2>10. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will
                notify you of any changes by:
              </p>
              <ul>
                <li>Posting the new Privacy Policy on this page</li>
                <li>Updating the "Effective Date" at the top</li>
                <li>
                  Sending you an email notification (for material changes)
                </li>
              </ul>
              <p>
                You are advised to review this Privacy Policy periodically for any
                changes. Changes are effective when posted on this page.
              </p>
            </section>

            <section className="legal-section">
              <h2>11. Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this
                Privacy Policy or our data practices, please contact us:
              </p>
              <div className="legal-contact">
                <p>
                  <strong>Optimus OS, Inc.</strong>
                </p>
                <p>Delaware Corporation</p>
                <p>
                  Email:{" "}
                  <a href="mailto:support@osoptimus.com">support@osoptimus.com</a>
                </p>
                <p>
                  Privacy Officer:{" "}
                  <a href="mailto:privacy@osoptimus.com">privacy@osoptimus.com</a>
                </p>
              </div>

              <p>
                For GDPR-related inquiries or to exercise your rights under GDPR,
                please include "GDPR Request" in your email subject line.
              </p>
            </section>

            <div className="legal-footer">
              <p>
                <strong>
                  COPYRIGHT © 2026 Optimus OS, Inc. – All Rights Reserved.
                </strong>
              </p>
              <p className="legal-update-note">Last Updated: May 15, 2026</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Legal;
